import React, { useRef, useState, useEffect } from 'react'
import { OfferBlock, OfferBlockText } from '../../models/shared/Offer'
import ContentEditable from 'react-contenteditable';
import BlockToolbar, { ColorPicker, TextAlignmentControls, FontWeightToggle, FontSizePicker } from './BlockToolbar';

interface Props {
    block: OfferBlock,
    readOnly?: boolean;
    onBlockUpdate: (updatedBlock: OfferBlock) => void;
    onBlockDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    autoFocus?: boolean;
}

export default function BlockText(props: Props) {
    const { block: generalBlock, onBlockUpdate } = props;
    const block = generalBlock as OfferBlockText;
    const [showToolbar, setShowToolbar] = useState(false);
    const contentRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (props.autoFocus && contentRef.current) {
            contentRef.current.click();
            contentRef.current.focus();
        }
    }, [props.autoFocus]);

    const handleBlockUpdate = (update: Partial<OfferBlockText>) => {
        const updatedBlock = {
            ...block,
            ...update
        };
        updatedBlock.content = (updatedBlock.content || '').replace(/\n/, '<br />');
        onBlockUpdate(updatedBlock);
    }

    return (
        <div className="position-relative w-100">
            <BlockToolbar
                contentRef={contentRef}
                onDelete={props.onBlockDelete}
                onMoveUp={props.onMoveUp}
                onMoveDown={props.onMoveDown}
                showToolbar={showToolbar}
                setShowToolbar={setShowToolbar}
                readOnly={props.readOnly}
            >
                <FontSizePicker
                    value={block.fontSize || 16}
                    onChange={(fontSize) => handleBlockUpdate({ fontSize })}
                />

                <ColorPicker
                    value={block.color || '#000000'}
                    onChange={(color) => handleBlockUpdate({ color })}
                />

                <TextAlignmentControls
                    value={block.textAlign}
                    onChange={(textAlign) => handleBlockUpdate({ textAlign })}
                />

                <FontWeightToggle
                    value={block.fontWeight}
                    onChange={(fontWeight) => handleBlockUpdate({ fontWeight })}
                />
            </BlockToolbar>

            <ContentEditable
                innerRef={contentRef}
                html={block.content || ''}
                onChange={(e) => handleBlockUpdate({ content: e.target.value })}
                onClick={props.readOnly ? undefined : () => setShowToolbar(true)}
                tagName='p'
                className='py-1'
                disabled={props.readOnly}
                style={{
                    margin: 0,
                    fontSize: block.fontSize ? `${block.fontSize}px` : undefined,
                    color: block.color || undefined,
                    textAlign: block.textAlign || undefined,
                    fontWeight: block.fontWeight || undefined,
                    userSelect: 'text', // Enable text selection within the content
                }}
            />
        </div>
    );
}
